import React from 'react';
import { Empty, Menu } from 'antd';
import cn from 'classnames';
import SearchField from '../SearchField/SearchField';
import AllSelection from '../AllSelection/AllSelection';
import { selectors, stateKeys } from '../../model';
import { useStoreSelector } from '../../hooks/useStoreSelector';
import Actions from '../Actions/Actions';
import VirtualizedOptions from '../VirtualizedOptions/VirtualizedOptions';
import Options from '../Options/Options';
import { isFn } from '../../utils';
import styles from './OptionsContainer.module.scss';

const { selectValue } = selectors;

const getSelector = (key) => (state) => selectValue(state, key);

const optionsCountSelector = getSelector(stateKeys.optionsCount);
const multipleSelector = getSelector(stateKeys.multiple);
const filteredOptionsSelector = getSelector(stateKeys.filteredOptions);
const hasRenderActionsSelector = (state) =>
  isFn(state[stateKeys.renderActions]);
const showSelectionActionsSelector = getSelector(
  stateKeys.showSelectionActions
);

const MAX_OPTIONS_SIZE_FOR_SEARCH = 10;
const MAX_OPTIONS_SIZE_FOR_SELECTION = 2;
const VIRTUALIZATION_THRESHOLD = 50;

const Content = ({ count, className, ...props }) => {
  const multiple = useStoreSelector(multipleSelector);
  const filteredOptions = useStoreSelector(filteredOptionsSelector);
  const hasRenderActions = useStoreSelector(hasRenderActionsSelector);
  const showSelectionActions = useStoreSelector(showSelectionActionsSelector);

  const showSearch = count >= MAX_OPTIONS_SIZE_FOR_SEARCH;
  const showSelection =
    multiple &&
    (showSelectionActions || count > MAX_OPTIONS_SIZE_FOR_SELECTION);
  const isVirtual = filteredOptions.length > VIRTUALIZATION_THRESHOLD;
  const showActions = hasRenderActions || multiple;

  return (
    <Menu {...props} className={cn(styles.root, className)}>
      {showSearch || showSelection ? (
        <Menu.Item className={cn(styles.item, styles.header)}>
          {showSearch ? <SearchField /> : null}
          {showSelection ? <AllSelection /> : null}
        </Menu.Item>
      ) : null}

      {filteredOptions.length ? (
        <Menu.Item
          className={cn(styles.item, styles.options, {
            [styles.virtualized]: isVirtual,
          })}
        >
          {isVirtual ? (
            <VirtualizedOptions options={filteredOptions} />
          ) : (
            <Options options={filteredOptions} />
          )}
        </Menu.Item>
      ) : (
        <Menu.Item className={styles.item}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Ничего не найдено"
          />
        </Menu.Item>
      )}

      {showActions ? (
        <Menu.Item className={cn(styles.item, styles.footer)}>
          <Actions />
        </Menu.Item>
      ) : null}
    </Menu>
  );
};

const OptionsContainer = ({ children, className, ...props }) => {
  const optionsCount = useStoreSelector(optionsCountSelector);

  return optionsCount ? (
    <Content {...props} count={optionsCount} />
  ) : (
    <Menu {...props} className={cn(styles.root, className)}>
      <Menu.Item className={styles.item}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />
      </Menu.Item>
    </Menu>
  );
};

export default OptionsContainer;
